/**
 * Team Member Card Component
 * Task 5.3: Create Team Members List Component
 */

import React from 'react';
import { Mail, Phone, Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import type { TeamMemberWithUser, TeamRole } from '@/types/teamMember.types';
import { ROLE_LABELS, ROLE_COLORS } from '@/types/teamMember.types';

interface TeamMemberCardProps {
  member: TeamMemberWithUser;
  canEdit: boolean;
  canRemove?: boolean;
  removeDisabledReason?: string;
  onEdit: (member: TeamMemberWithUser) => void;
  onRemove: (member: TeamMemberWithUser) => void;
}

export const TeamMemberCard: React.FC<TeamMemberCardProps> = ({
  member,
  canEdit,
  canRemove = true,
  removeDisabledReason,
  onEdit,
  onRemove
}) => {
  const roleColor = ROLE_COLORS[member.role as TeamRole];

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold text-lg">{member.user.full_name}</h3>
              <span className={`px-2 py-1 text-xs font-medium rounded-full border ${roleColor}`}>
                {ROLE_LABELS[member.role as TeamRole]}
              </span>
              {member.is_celebration_creator && (
                <span className="px-2 py-1 text-xs font-medium rounded-full border bg-slate-100 text-slate-700 border-slate-200">
                  Creator
                </span>
              )}
            </div>
            
            <div className="space-y-1 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                <span>{member.user.email}</span>
              </div>
              
              {member.user.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>{member.user.phone}</span>
                </div>
              )}

              {member.role === 'host' && (
                <div className="text-xs text-amber-700 pt-1">
                  Celebration quota:{' '}
                  {member.celebration_guest_quota === null || member.celebration_guest_quota === undefined
                    ? 'Unlimited'
                    : `${member.celebration_guest_quota} seats`}
                </div>
              )}

              {member.is_celebration_creator && (
                <div className="text-xs text-slate-500 pt-1">
                  The celebration creator must stay active and can only be Organizer or Event Manager.
                </div>
              )}
            </div>
          </div>

          {canEdit && (
            <div className="flex gap-2 ml-4">
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => onEdit(member)}
                className="h-8 w-8 p-0"
              >
                <Edit className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => onRemove(member)}
                className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 disabled:text-gray-400 disabled:hover:bg-transparent"
                disabled={!canRemove}
                title={!canRemove ? removeDisabledReason : 'Remove team member'}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
